import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined
} from '@ant-design/icons';
import { 
  Button, 
  Input, 
  Select, 
  Table, 
  Row, 
  Col, 
  Statistic, 
  Card,
  Space,
  Modal,
  message,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import quotesService, { Quote, StatusType } from '../services/quotesService';
import { formatCurrency, formatQuoteReference } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

// Composant pour le statut des devis
const QuoteStatusTag: React.FC<{ status: StatusType }> = ({ status }) => {
  const statusConfig = {
    draft: { color: '#d9d9d9', text: 'Brouillon' },
    sent: { color: '#52c41a', text: 'Envoyé' },
    accepted: { color: '#73d13d', text: 'Accepté' },
    rejected: { color: '#ff4d4f', text: 'Rejeté' },
    expired: { color: '#faad14', text: 'Expiré' },
  };

  const config = statusConfig[status] || { color: '#d9d9d9', text: status };
  
  return (
    <span style={{ 
      color: config.color, 
      backgroundColor: `${config.color}20`, 
      padding: '2px 8px', 
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500
    }}>
      {config.text}
    </span>
  );
};

// Composant pour les actions sur chaque devis
const QuoteActions: React.FC<{ quote: Quote; isAgent?: boolean; onActionComplete: () => void }> = ({ quote, isAgent, onActionComplete }) => {
  const navigate = useNavigate();

  const handleDelete = () => {
    Modal.confirm({
      title: 'Supprimer le devis',
      content: `Êtes-vous sûr de vouloir supprimer le devis ${quote.reference} ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          if (quote.id) {
            await quotesService.deleteQuote(quote.id);
            message.success('Devis supprimé avec succès');
            onActionComplete();
          }
        } catch (error) {
          message.error('Erreur lors de la suppression du devis');
        }
      },
    });
  };

  return (
    <Space size="small">
      <Tooltip title="Voir le devis">
        <Button 
          type="text" 
          icon={<EyeOutlined />} 
          size="small"
          onClick={() => navigate(`/devis/${quote.id}`)}
        />
      </Tooltip>
      {!isAgent && (
        <>
          <Tooltip title="Modifier le devis">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => navigate(`/devis/${quote.id}/modifier`)}
            />
          </Tooltip>
          <Tooltip title="Supprimer le devis">
            <Button 
              type="text" 
              icon={<DeleteOutlined />} 
              size="small"
              danger
              onClick={handleDelete}
            />
          </Tooltip>
        </>
      )}
    </Space>
  );
};

const QuotesPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusType | undefined>();

  // Charger les devis
  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const data = await quotesService.getQuotes({
        search: searchTerm,
        status: statusFilter,
        limit: 1000,
      });
      setQuotes(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des devis:', error);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  // Gestion des filtres
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleStatusFilter = (status: StatusType) => {
    setStatusFilter(status);
  };

  // Colonnes du tableau
  const columns: ColumnsType<Quote> = [
    {
      title: 'Référence',
      dataIndex: 'reference',
      key: 'reference',
      width: 140,
      render: (text: string, record: Quote) => (
        <Button 
          type="link" 
          style={{ padding: 0, fontSize: '14px', fontWeight: 500 }}
          onClick={() => navigate(`/devis/${record.id}`)}
        >
          {formatQuoteReference(text)}
        </Button>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 200,
      responsive: ['md'],
    },
    {
      title: 'Type',
      key: 'quote_type',
      width: 120,
      responsive: ['lg'],
      render: (record: Quote) => {
        if (record.diagnostic_report_id) {
          return (
            <span style={{ 
              color: '#1890ff', 
              backgroundColor: '#e6f7ff', 
              padding: '2px 8px', 
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              Diagnostic
            </span>
          );
        }
        return (
          <span style={{ 
            color: '#52c41a', 
            backgroundColor: '#f6ffed', 
            padding: '2px 8px', 
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500
          }}>
            Standard
          </span>
        );
      },
    },
    {
      title: 'Intervention',
      dataIndex: 'intervention_id',
      key: 'intervention_id',
      width: 100,
      responsive: ['lg'],
      render: (id: number | undefined) => id ? `#${id}` : '-',
    },
    {
      title: 'Date d\'émission',
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 120,
      responsive: ['lg'],
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Échéance',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      responsive: ['lg'],
      render: (date: string) => {
        const isExpired = new Date(date) < new Date();
        return (
          <span style={{ color: isExpired ? '#ff4d4f' : 'inherit' }}>
            {new Date(date).toLocaleDateString('fr-FR')}
          </span>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: StatusType) => <QuoteStatusTag status={status} />,
    },
    {
      title: 'Paiement',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      responsive: ['md'],
      render: (paymentStatus: string | undefined, record: Quote) => {
        // Afficher le statut de paiement pour les devis acceptés
        if (record.status !== 'accepted') return '-';
        
        if (paymentStatus === 'paid') {
          return (
            <span style={{ 
              color: '#52c41a', 
              backgroundColor: '#f6ffed', 
              padding: '2px 8px', 
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              ✓ Payé
            </span>
          );
        }

        if (paymentStatus === 'deferred') {
          return (
            <Tooltip title={record.scheduled_date ? `Planifié le ${new Date(record.scheduled_date).toLocaleDateString('fr-FR')}` : 'Paiement différé'}>
              <span style={{ 
                color: '#1890ff', 
                backgroundColor: '#e6f7ff', 
                padding: '2px 8px', 
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                📅 Différé
              </span>
            </Tooltip>
          );
        }
        
        return (
          <span style={{ 
            color: '#faad14', 
            backgroundColor: '#fffbe6', 
            padding: '2px 8px', 
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500
          }}>
            ⏳ En attente
          </span>
        );
      },
    },
    {
      title: 'Date intervention',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 130,
      responsive: ['lg'],
      render: (date: string | undefined, record: Quote) => {
        if (!date) return '-';
        const scheduledDate = new Date(date);
        const isPast = scheduledDate < new Date();
        return (
          <Tooltip title={record.execute_now ? 'Exécution immédiate' : 'Planifié par le client'}>
            <span style={{ color: isPast ? '#ff4d4f' : '#1890ff' }}>
              {scheduledDate.toLocaleDateString('fr-FR')} {scheduledDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </Tooltip>
        );
      },
    },
    ...(!isAgent ? [
      {
        title: 'Montant HT',
        dataIndex: 'subtotal',
        key: 'subtotal',
        width: 120,
        responsive: ['sm'] as ('sm')[],
        render: (amount: number) => formatCurrency(amount),
        align: 'right' as const,
      },
      {
        title: 'Montant TTC',
        dataIndex: 'total',
        key: 'total',
        width: 120,
        render: (amount: number) => (
          <span style={{ fontWeight: 500 }}>
            {formatCurrency(amount)}
          </span>
        ),
        align: 'right' as const,
      },
    ] : []),
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Quote) => (
        <QuoteActions quote={record} isAgent={isAgent} onActionComplete={fetchQuotes} />
      ),
      align: 'center' as const,
    },
  ];

  // Calcul des statistiques
  const stats = [
    { 
      title: 'Total des devis', 
      value: quotes.length,
      color: '#1890ff'
    },
    { 
      title: 'Brouillons', 
      value: quotes.filter(q => q.status === 'draft').length,
      color: '#d9d9d9'
    },
    { 
      title: 'Envoyés', 
      value: quotes.filter(q => q.status === 'sent').length,
      color: '#52c41a'
    },
    { 
      title: 'Acceptés', 
      value: quotes.filter(q => q.status === 'accepted').length,
      color: '#73d13d'
    },
  ];

  return (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh',
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '16px',
        backgroundColor: '#fff',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Gestion des Devis</h1>
          <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
            Créez, gérez et suivez vos devis clients
          </p>
        </div>
        {!isAgent && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/devis/nouveau')}
          >
            Nouveau Devis
          </Button>
        )}
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {stats.map((stat, index) => (
          <Col xs={12} sm={6} key={index}>
            <Card size="small" style={{ borderRadius: '8px' }}>
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={{ 
                  color: stat.color,
                  fontSize: '18px',
                  fontWeight: 600
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: '16px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Input
              placeholder="Rechercher par référence ou client..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              allowClear
              onChange={handleStatusFilter}
            >
              <Option value="draft">Brouillon</Option>
              <Option value="sent">Envoyé</Option>
              <Option value="accepted">Accepté</Option>
              <Option value="rejected">Rejeté</Option>
              <Option value="expired">Expiré</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button 
              type="default"
              style={{ width: '100%' }}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter(undefined);
              }}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des devis */}
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={quotes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range) => 
              `${range[0]}-${range[1]} sur ${total} devis`,
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default QuotesPage;